export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export const validateRequired = (value: string | undefined, fieldName: string): ValidationResult => {
  if (!value || value.trim() === '') {
    return { isValid: false, message: `${fieldName} is required` };
  }
  return { isValid: true };
};

export const validateEmail = (email: string | undefined): ValidationResult => {
  if (!email) {
    return { isValid: false, message: 'Email is required' };
  }
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email)) {
    return { isValid: false, message: 'Invalid email format' };
  }
  return { isValid: true };
};

export const validatePhone = (phone: string | undefined): ValidationResult => {
  if (!phone) {
    return { isValid: false, message: 'Phone number is required' };
  }
  const phonePattern = /^\d{10}$/;
  if (!phonePattern.test(phone)) {
    return { isValid: false, message: 'Phone number must be 10 digits' };
  }
  return { isValid: true };
};

export const validatePassword = (password: string | undefined): ValidationResult => {
  if (!password) {
    return { isValid: false, message: 'Password is required' };
  }
  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long' };
  }
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }
  if (!/[0-9]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one number' };
  }
  return { isValid: true };
};

export const validateConfirmPassword = (password: string | undefined, confirmPassword: string | undefined): ValidationResult => {
  if (!confirmPassword) {
    return { isValid: false, message: 'Confirm password is required' };
  }
  if (password !== confirmPassword) {
    return { isValid: false, message: 'Passwords do not match' };
  }
  return { isValid: true };
};

export const isValidEmail = (email: string): boolean => {
  return validateEmail(email).isValid;
};

export const isValidPhone = (phone: string): boolean => {
  return validatePhone(phone).isValid;
};

export const validateURL = (url: string | undefined): ValidationResult => {
  if (!url || url.trim() === '') {
    return { isValid: true }; // URL is optional
  }
  try {
    new URL(url);
    return { isValid: true };
  } catch {
    return { isValid: false, message: 'Invalid URL format' };
  }
};

export const validatePinCode = (pinCode: string | undefined): ValidationResult => {
  if (!pinCode || pinCode.trim() === '') {
    return { isValid: true }; // Pin code is optional
  }
  const pinPattern = /^\d{6}$/;
  if (!pinPattern.test(pinCode)) {
    return { isValid: false, message: 'Pin code must be 6 digits' };
  }
  return { isValid: true };
};

export const validateDescription = (description: string | undefined): ValidationResult => {
  if (!description || description.trim() === '') {
    return { isValid: true }; // Description is optional
  }
  if (description.length > 500) {
    return { isValid: false, message: 'Description must be less than 500 characters' };
  }
  return { isValid: true };
};

export const validateAddress = (address: string | undefined): ValidationResult => {
  if (!address || address.trim() === '') {
    return { isValid: true }; // Address is optional
  }
  if (address.length > 200) {
    return { isValid: false, message: 'Address must be less than 200 characters' };
  }
  return { isValid: true };
};